import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white rounded-xl shadow-sm hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20 active:shadow-none",
        destructive:
          "bg-red-500 text-white rounded-xl shadow-sm hover:bg-red-600 active:shadow-none",
        outline:
          "border-1.5 border-border bg-background rounded-xl hover:bg-muted hover:border-primary/40 active:bg-muted/80",
        secondary:
          "bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80",
        ghost:
          "rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground",
        accent:
          "bg-accent text-white rounded-xl shadow-sm hover:bg-accent/90 hover:shadow-md hover:shadow-accent/20",
        link:
          "text-primary underline-offset-4 hover:underline p-0 h-auto shadow-none",
        // Gradient tied to the actual --primary CSS variable via
        // color-mix() (top: the real brand color, bottom: the same color
        // mixed toward black) rather than separately hand-picked blue-500/
        // blue-700 stops — one source of truth for the brand color, not
        // two that could silently drift out of sync with each other.
        premium:
          "bg-[linear-gradient(to_bottom,hsl(var(--primary)),color-mix(in_srgb,hsl(var(--primary))_75%,black))] text-white rounded-xl shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/30 hover:brightness-110",
      },
      size: {
        default: "h-10 px-5 text-sm [&_svg]:size-4",
        sm:      "h-8 px-3.5 text-xs rounded-lg [&_svg]:size-3.5",
        lg:      "h-11 px-6 text-sm rounded-xl [&_svg]:size-4",
        xl:      "h-12 px-7 text-base rounded-2xl [&_svg]:size-5",
        icon:    "h-9 w-9 rounded-xl [&_svg]:size-4",
        "icon-sm": "h-7 w-7 rounded-lg [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
})
Button.displayName = "Button"

export { Button, buttonVariants }
